import React from 'react';
import Container from 'saur_ui_library/src/Atoms/Container/Container';
import Word from 'saur_ui_library/src/Atoms/Word/Word';
import { v4 as uuidv4 } from 'uuid';

// Types pour la factorisation
interface PopupStyleConfig {
  maxWidth: string;
  backgroundColor?: string;
  dataAttribute: string;
}

interface PopupContainerProps {
  title: string | (string | undefined)[];
  titleValue?: string;
  dateRemontee?: string;
  children: React.ReactNode;
  styleConfig: PopupStyleConfig;
  showDateSection?: boolean;
}

interface TableProps {
  tableElements?: {};
  theadElements?: (string | undefined)[];
  cellStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
}

// Sous-composant : Injection de styles CSS pour ArcGIS
const injectPopupStyles = (styleConfig: PopupStyleConfig) => {
  if (typeof document === 'undefined' || !document.head) {
    return;
  }

  if (document.head.querySelector(`style[${styleConfig.dataAttribute}]`)) {
    return;
  }

  const style = document.createElement('style');
  style.textContent = `
    .esri-popup__main-container {
      width: auto !important;
      min-width: auto !important;
      max-width: ${styleConfig.maxWidth} !important;
      border-radius: 12px !important;
      overflow: visible !important;
      height: auto !important;
      max-height: 600px !important;
    }
    .esri-ui .esri-popup {
      border-radius: 12px !important;
      width: auto !important;
      min-width: auto !important;
      max-width: ${styleConfig.maxWidth} !important;
    }
  `;
  style.setAttribute(styleConfig.dataAttribute, 'true');
  document.head.appendChild(style);
};

// Sous-composant : Header de popup réutilisable
const PopupHeader: React.FC<{
  title: string | (string | undefined)[];
  titleValue?: string;
  backgroundColor?: string;
}> = ({ title, titleValue, backgroundColor = '#f8f9fa' }) => (
  <div style={{
    padding: '12px 16px',
    fontWeight: 'bold',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: '1px solid #e9ecef',
    backgroundColor
  }}>
    {typeof title === 'string' ? (
      <span>{title}</span>
    ) : (
      <>
        <img
          src={`data:image/svg+xml,${encodeURIComponent(title[0] as string)}`}
          alt='SVG'
          style={{ width: '20px', height: '20px' }}
        />
        <span>{title[1]}</span>
      </>
    )}
    {titleValue && <span style={{ marginLeft: 'auto', color: '#6c757d' }}>{titleValue}</span>}
  </div>
);

// Sous-composant : Section date réutilisable
const DateSection: React.FC<{
  dateRemontee?: string;
  module?: string;
  indicator?: string | null;
}> = ({ dateRemontee, module, indicator }) => {
  if (!dateRemontee || indicator === 'Terrain') return null;
  
  return (
    <div style={{
      padding: '10px 16px',
      borderBottom: '1px solid #e9ecef',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <span style={{ color: '#495057', fontSize: 'smaller' }}>Date de la catégorisation</span>
      <span style={{ fontWeight: 'bold', color: '#212529' }}>
        {dateRemontee !== 'Null' 
          ? (module === 'pluie' ? dateRemontee.slice(3) : dateRemontee) 
          : 'Pas de télégestion'
        }
      </span>
    </div>
  );
};

// Sous-composant : Tableau générique réutilisable
const GenericTable: React.FC<TableProps> = ({
  tableElements,
  theadElements,
  cellStyle = { padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' },
  headerStyle = { padding: '8px 12px', border: '1px solid #dee2e6', fontWeight: 'bold', textAlign: 'left', color: '#495057' }
}) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{
      width: '100%',
      fontSize: '12px',
      borderCollapse: 'collapse',
      border: '1px solid #dee2e6'
    }}>
      {theadElements && (
        <thead style={{ backgroundColor: '#f8f9fa' }}>
          <tr>
            {theadElements.map((el) => (
              <th key={uuidv4()} style={headerStyle}>
                {el}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {Object.entries(tableElements || {}).map(([key, value]) => (
          <tr key={uuidv4()} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#fff' }}>
            <th scope='row' style={{
              padding: '8px 12px',
              border: '1px solid #dee2e6',
              fontWeight: '500',
              backgroundColor: '#f8f9fa',
              color: '#495057',
              textAlign: 'left'
            }}>
              {key}
            </th>
            {typeof value === 'object' ? (
              (value as []).map((val: any) => (
                <td key={uuidv4()} style={cellStyle}>
                  {typeof val === 'string' && val.startsWith('<svg') ? (
                    <img
                      src={`data:image/svg+xml,${encodeURIComponent(val)}`}
                      alt='SVG'
                      style={{ width: '16px', height: '16px' }}
                    />
                  ) : (
                    val
                  )}
                </td>
              ))
            ) : (
              <td style={cellStyle}>
                {value as React.ReactNode}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Sous-composant : Section de données avec tableau compact
const DataSection: React.FC<{
  title: string;
  elements?: {};
  filterCondition?: (index: number) => boolean;
  showBorder?: boolean;
}> = ({ 
  title, 
  elements, 
  filterCondition = () => true,
  showBorder = true 
}) => (
  <div style={{ 
    padding: '12px 16px', 
    borderBottom: showBorder ? '1px solid #e9ecef' : 'none' 
  }}>
    <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#212529' }}>{title}</h3>
    {elements && (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '10vw', fontSize: '11px', borderCollapse: 'collapse' }}>
          <tbody>
            {Object.entries(elements).map(([key, value], index) => {
              if (filterCondition(index)) {
                return (
                  <tr key={uuidv4()}>
                    {(value as string[]).map((val: string) => (
                      <td key={uuidv4()} style={{ padding: '2px 4px' }}>{val}</td>
                    ))}
                  </tr>
                );
              }
              return null;
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// Sous-composant : Container principal de popup réutilisable
const PopupContainer: React.FC<PopupContainerProps> = ({
  title,
  titleValue,
  dateRemontee,
  children,
  styleConfig,
  showDateSection = true
}) => {
  injectPopupStyles(styleConfig);

  return (
    <div style={{
      fontFamily: 'Monserrat, sans-serif',
      fontSize: '14px',
      height: 'auto',
      width: 'auto',
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      <PopupHeader 
        title={title} 
        titleValue={titleValue} 
        backgroundColor={styleConfig.backgroundColor}
      />
      {showDateSection && (
        <DateSection dateRemontee={dateRemontee} />
      )}
      <div style={{ padding: '16px' }}>
        {children}
      </div>
    </div>
  );
};

// Container spécialisé pour les popups DO utilisant les sous-composants factoriés
const renderDoPopupContainer = (
  title: string | (string | undefined)[],
  titleValue?: string,
  tableElements?: {},
  elements?: {},
  dateRemontee?: string,
  icon?: string
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: 'none',
    dataAttribute: 'data-popup-width'
  };

  return (
    <PopupContainer
      title={title}
      titleValue={titleValue}
      dateRemontee={dateRemontee}
      styleConfig={styleConfig}
    >
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <div style={{ minWidth: '200px' }}>
          {tableElements && Object.entries(tableElements).map(([key, value]) => (
            <div key={uuidv4()} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 0',
              borderBottom: '1px solid #f1f3f4',
              minWidth: '200px'
            }}>
              <span style={{ color: '#495057', fontWeight: '500', fontSize: 'smaller' }}>{key}</span>
              <span style={{ fontWeight: 'bold', color: '#212529', marginLeft: '20px' }}>{value as string}</span>
            </div>
          ))}
        </div>
        {icon && (
          <div style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '50px',
            height: '50px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px'
          }}>
            <img
              src={`data:image/svg+xml,${encodeURIComponent(icon)}`}
              alt='SVG'
              style={{ width: '30px', height: '30px' }}
            />
          </div>
        )}
      </div>
      {elements && (
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          {Object.entries(elements).map(([key, value]) => (
            <div key={uuidv4()} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              backgroundColor: '#ffffff',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
              fontSize: '12px'
            }}>
              <img
                src={`data:image/svg+xml,${encodeURIComponent(String(value))}`}
                alt='SVG'
                style={{ width: '14px', height: '14px' }}
              />
              <span style={{ fontWeight: 'bold', color: '#212529' }}>{key}</span>
            </div>
          ))}
        </div>
      )}
    </PopupContainer>
  );
}

// Container spécialisé pour les popups INDUS utilisant les sous-composants factoriés
const renderIndusPopupContainer = (
  title: string | (string | undefined)[],
  titleValue?: string,
  tableElements?: {},
  theadElements?: (string | undefined)[],
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: '450px',
    backgroundColor: '#f8f9fa',
    dataAttribute: 'data-popup-width-indus'
  };

  return (
    <PopupContainer
      title={title}
      titleValue={titleValue}
      styleConfig={styleConfig}
      showDateSection={false}
    >
      <GenericTable 
        tableElements={tableElements}
        theadElements={theadElements}
      />
    </PopupContainer>
  );
}

// Container spécialisé pour les popups HYDRO utilisant les sous-composants factoriés
const renderHydroPopupContainer = (
  title: string | (string | undefined)[],
  titleValue?: string,
  tableElements?: {},
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: '320px',
    backgroundColor: '#e8f4fd',
    dataAttribute: 'data-popup-width-hydro'
  };

  return (
    <PopupContainer
      title={title}
      titleValue={titleValue}
      styleConfig={styleConfig}
      showDateSection={false}
    >
      <GenericTable 
        tableElements={tableElements}
        headerStyle={{
          padding: '8px 12px',
          border: '1px solid #dee2e6',
          fontWeight: '500',
          backgroundColor: '#e8f4fd',
          color: '#495057',
          textAlign: 'left'
        }}
      />
    </PopupContainer>
  );
}

// Container spécialisé pour les popups PLUIE utilisant les sous-composants factoriés
const renderPluiePopupContainer = (
  title: string | (string | undefined)[],
  tableElements?: {},
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: '400px',
    backgroundColor: '#e3f2fd',
    dataAttribute: 'data-popup-width-pluie'
  };

  return (
    <PopupContainer
      title={title}
      styleConfig={styleConfig}
      showDateSection={false}
    >
      <GenericTable 
        tableElements={tableElements}
        headerStyle={{
          padding: '8px 12px',
          border: '1px solid #dee2e6',
          fontWeight: '500',
          backgroundColor: '#f8fafe',
          color: '#495057',
          textAlign: 'left'
        }}
      />
    </PopupContainer>
  );
}

// Container spécialisé pour les popups MILIEU utilisant les sous-composants factoriés
const renderMilieuPopupContainer = (
  title: string | (string | undefined)[],
  tableElements?: {},
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: 'none',
    dataAttribute: 'data-popup-width-milieu'
  };

  return (
    <PopupContainer
      title={title}
      styleConfig={styleConfig}
      showDateSection={false}
    >
      <div style={{ maxHeight: '400px', overflowY: 'auto', justifyContent: 'center', display: 'flex' }}>
        <div style={{ minWidth: '200px' }}>
          {tableElements && Object.entries(tableElements).map(([key, value]) => (
            <div key={uuidv4()} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 0',
              borderBottom: '1px solid #f1f3f4',
              minWidth: '200px'
            }}>
              <span style={{ color: '#495057', fontWeight: '500', fontSize: 'smaller' }}>{key}</span>
              <span style={{ fontWeight: 'bold', color: '#212529', marginLeft: '20px' }}>{value as string}</span>
            </div>
          ))}
        </div>
      </div>
    </PopupContainer>
  );
}

// Container spécialisé pour les popups MR (Mesure Réseau) utilisant les sous-composants factoriés
const renderMrPopupContainer = (
  title: string | (string | undefined)[],
  dateRemontee?: string,
  elements?: {},
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: '300px',
    dataAttribute: 'data-popup-width-mr'
  };

  return (
    <PopupContainer
      title={title}
      dateRemontee={dateRemontee}
      styleConfig={styleConfig}
      showDateSection={true}
    >
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {elements && Object.entries(elements).map(([key, value]) => (
          <div key={uuidv4()} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 0',
            borderBottom: '1px solid #f1f3f4',
            minWidth: '200px'
          }}>
            <span style={{ color: '#495057', fontWeight: '500', fontSize: 'smaller' }}>{key}</span>
            <span style={{ fontWeight: 'bold', color: '#212529', marginLeft: '20px' }}>{value as string}</span>
          </div>
        ))}
      </div>
    </PopupContainer>
  );
}

// Container spécialisé pour les popups PR-MR (reste complexe, garde l'implémentation complète)
const renderPrMrPopupContainer = (
  title: string | (string | undefined)[],
  titleValue?: string,
  tableElements?: {},
  elements?: {},
  dateRemontee?: string,
  volume?: [string, string, string],
  warnings?: string,
  pompesSum?: number
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: '300px',
    dataAttribute: 'data-popup-width-pr'
  };

  injectPopupStyles(styleConfig);

  return (
    <div style={{
      fontFamily: 'Monserrat, sans-serif',
      fontSize: '14px',
      height: 'auto',
      width: 'auto',
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      <PopupHeader title={title} />
      <DateSection dateRemontee={dateRemontee} />

      {/* Section Temps et Nb démarrage */}
      <div style={{
        padding: '12px 16px',
        display: 'flex',
        gap: '16px',
        borderBottom: '1px solid #e9ecef'
      }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#495057' }}>Temps</h4>
          {pompesSum && pompesSum > 0 ? (
            Object.entries(tableElements || {}).map(([key, value]) => {
              if (key === '0') {
                return (
                  <div key={uuidv4()}>
                    {Array.from(value as any[])
                      .slice(0, pompesSum)
                      .map((val: any) => (
                        <div key={uuidv4()} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '2px 0'
                        }}>
                          {val && val[0] && (
                            <img
                              src={`data:image/svg+xml,${encodeURIComponent(val[0])}`}
                              alt='SVG'
                              style={{ 
                                width: '14px', 
                                height: '14px',
                                display: 'block'
                              }}
                              onError={(e) => {
                                console.log('Erreur chargement SVG:', val[0]);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <span style={{ fontSize: '11px' }}>{val && val[1] ? val[1] : ''}</span>
                          <span style={{ fontWeight: 'bold', fontSize: '11px' }}>{val && val[2] ? val[2] : ''}</span>
                        </div>
                      ))}
                  </div>
                );
              }
              return null;
            })
          ) : (
            <span style={{ fontSize: '11px', color: '#dc3545' }}>
              Aucune pompe !
            </span>
          )}
        </div>
        
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#495057' }}>Nb démarrage</h4>
          {pompesSum && pompesSum > 0 ? (
            Object.entries(tableElements || {}).map(([key, value]) => {
              if (key === '1') {
                return (
                  <div key={uuidv4()}>
                    {Array.from(value as any[])
                      .slice(0, pompesSum)
                      .map((val: any) => (
                        <div key={uuidv4()} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '2px 0'
                        }}>
                          {val && val[0] && (
                            <img
                              src={`data:image/svg+xml,${encodeURIComponent(val[0])}`}
                              alt='SVG'
                              style={{ 
                                width: '14px', 
                                height: '14px',
                                display: 'block'
                              }}
                              onError={(e) => {
                                console.log('Erreur chargement SVG:', val[0]);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <span style={{ fontSize: '11px' }}>{val && val[1] ? val[1] : ''}</span>
                          <span style={{ fontWeight: 'bold', fontSize: '11px' }}>{val && val[2] ? val[2] : ''}</span>
                        </div>
                      ))}
                  </div>
                );
              }
              return null;
            })
          ) : (
            <span style={{ fontSize: '11px', color: '#dc3545' }}>
              Aucune pompe !
            </span>
          )}
        </div>
      </div>

      {/* Autres sections... (volumes, débordement, pluie, warnings) */}
      {volume && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e9ecef' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#212529' }}>Volumes</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0', width: '10vw' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                {volume.length > 1 && (
                  <img src={`data:image/svg+xml,${encodeURIComponent(volume[1])}`} alt='SVG' style={{ width: '14px', height: '14px' }} />
                )}
                <span style={{ fontSize: '11px' }}>Vol. pompé télégéré</span>
              </div>
              <span style={{ fontWeight: 'bold', fontSize: '11px' }}>{volume[0]}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0', width: '10vw' }}>
              <span style={{ fontSize: '11px', marginLeft: '20px' }}>Vol. pompé calculé</span>
              <span style={{ fontSize: '11px' }}>{volume[2]}</span>
            </div>
          </div>
        </div>
      )}

      {/* Section Débordement */}
      <DataSection 
        title="Débordement"
        elements={elements}
        filterCondition={(index) => index > 1}
      />

      {/* Section Pluie */}
      <DataSection 
        title="Pluie"
        elements={elements}
        filterCondition={(index) => index < 2}
      />

      {warnings && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
          <span style={{ fontSize: '11px', color: '#856404' }}>{warnings}</span>
        </div>
      )}
    </div>
  );
}

// Container spécialisé pour les popups H2S (reste complexe, garde l'implémentation spécialisée)
const renderH2sPopupContainer = (
  title: string | (string | undefined)[],
  titleValue?: string,
  tableElements?: {},
  indicator?: string | null,
  dateRemontee?: string
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: '350px',
    dataAttribute: 'data-popup-width-h2s'
  };

  return (
    <PopupContainer
      title={title}
      titleValue={titleValue}
      dateRemontee={dateRemontee}
      styleConfig={styleConfig}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Object.entries(tableElements || {}).map(([key, value]: Array<any>) => (
          <div key={uuidv4()} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: key?.includes(indicator) ? '#e3f2fd' : '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            borderLeft: key?.includes(indicator) ? '4px solid #007ac2' : '1px solid #dee2e6'
          }}>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px' }}>
              {value[0] && typeof value[0] === "string" && value[0].startsWith("<svg") && (
                <img 
                  src={`data:image/svg+xml,${encodeURIComponent(value[0])}`} 
                  alt='SVG' 
                  style={{ width: '18px', height: '18px' }} 
                />
              )}
            </div>
            <span style={{
              fontSize: '13px',
              color: '#495057',
              fontWeight: key?.includes(indicator) ? 'bold' : 'normal',
              flex: 1
            }}>
              {key} :
            </span>
            <span style={{
              fontSize: '13px',
              fontWeight: 'bold',
              color: key?.includes(indicator) ? '#007ac2' : '#212529',
              backgroundColor: key?.includes(indicator) ? '#ffffff' : 'transparent',
              padding: key?.includes(indicator) ? '2px 6px' : '0',
              borderRadius: key?.includes(indicator) ? '4px' : '0'
            }}>
              {value[1] !== 'Null' ? value[1] : 'NR'}
            </span>
          </div>
        ))}
      </div>
    </PopupContainer>
  );
}

// Container spécialisé pour les popups ECP (reste complexe avec ILI)
const renderEcpPopupContainer = (
  title: string | (string | undefined)[],
  titleValue?: string,
  tableElements?: {},
  theadElements?: (string | undefined)[],
  iliElements?: any[],
  className?: string,
  module?: string,
  indicator?: string | null,
  dateRemontee?: string
) => {
  const styleConfig: PopupStyleConfig = {
    maxWidth: '400px',
    dataAttribute: 'data-popup-width-ecp'
  };

  injectPopupStyles(styleConfig);

  return (
    <div style={{
      fontFamily: 'Monserrat, sans-serif',
      fontSize: '14px',
      height: 'auto',
      width: 'auto',
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      <PopupHeader title={title} />
      <DateSection dateRemontee={dateRemontee} module={module} indicator={indicator} />

      {module !== 'H2S' && (
        <div style={{ padding: '16px' }}>
          <GenericTable 
            tableElements={tableElements}
            theadElements={theadElements}
          />

          {iliElements?.length !== 0 && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              borderTop: '2px solid #007ac2',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px'
            }}>
              <div style={{ display: 'grid', gap: '6px' }}>
                {iliElements!.map((el) => (
                  <div key={uuidv4()} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    backgroundColor: '#ffffff',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    <span style={{ fontWeight: '600', color: '#495057', fontSize: '11px' }}>
                      {el.title} :
                    </span>
                    <span style={{ color: '#212529', fontSize: '11px' }}>
                      {el.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const RenderDoTpElements = ({
  label,
  keyIdentifier,
  tableElements,
  pompesSum,
}) => {
  return (
    <Container>
      <Word value={label} />
      {pompesSum > 0 ? (
        Object.entries(tableElements).map(([key, value]) => {
          if (key === keyIdentifier) {
            return (
              <React.Fragment key={uuidv4()}>
                {Array.from(value as string)
                  .slice(0, pompesSum)
                  .map((val) => (
                    <div className='time_number' key={uuidv4()}>
                      <img
                        src={`data:image/svg+xml,${encodeURIComponent(val[0])}`}
                        alt='SVG'
                      />
                      <span>{`${val[1]}`}</span>{' '}
                      <span className='value'>{val[2]}</span>
                    </div>
                  ))}
              </React.Fragment>
            );
          }
          return null;
        })
      ) : (
        <span className='no_pompe'>
          Aucune <br /> pompe !
        </span>
      )}
    </Container>
  );
};

// Composant PLUIE Popup Container utilisant les fonctions de rendu existantes
export const PluiePopupContainer = ({
  title,
  tableElements,
}: {
  title: string | (string | undefined)[];
  tableElements?: {};
}) => {
  return renderPluiePopupContainer(title, tableElements);
};

// Composant MILIEU Popup Container utilisant les fonctions de rendu existantes
export const MilieuPopupContainer = ({
  title,
  tableElements,
}: {
  title: string | (string | undefined)[];
  tableElements?: {};
}) => {
  return renderMilieuPopupContainer(title, tableElements);
};

// Composant DO Popup Container utilisant les fonctions de rendu existantes
export const DoPopupContainer = ({
  title,
  titleValue,
  tableElements,
  elements,
  dateRemontee,
  icon,
}: {
  title: string | (string | undefined)[];
  titleValue?: string;
  tableElements?: {};
  elements?: {};
  dateRemontee?: string;
  icon?: string;
}) => {
  return renderDoPopupContainer(title, titleValue, tableElements, elements, dateRemontee, icon);
};

// Composant PR-MR Popup Container utilisant les fonctions de rendu existantes
export const PrMrPopupContainer = ({
  title,
  titleValue,
  tableElements,
  elements,
  dateRemontee,
  volume,
  warnings,
  pompesSum,
}: {
  title: string | (string | undefined)[];
  titleValue?: string;
  tableElements?: {};
  elements?: {};
  dateRemontee?: string;
  volume?: [string, string, string];
  warnings?: string;
  pompesSum?: number;
}) => {
  return renderPrMrPopupContainer(title, titleValue, tableElements, elements, dateRemontee, volume, warnings, pompesSum);
};

// Composant MR (Mesure Réseau) Popup Container utilisant les fonctions de rendu existantes
export const MrPopupContainer = ({
  title,
  dateRemontee,
  elements,
}: {
  title: string | (string | undefined)[];
  dateRemontee?: string;
  elements?: {};
}) => {
  return renderMrPopupContainer(title, dateRemontee, elements);
};

// Composant INDUS Popup Container utilisant les fonctions de rendu existantes
export const IndusPopupContainer = ({
  title,
  titleValue,
  tableElements,
  theadElements,
}: {
  title: string | (string | undefined)[];
  titleValue?: string;
  tableElements?: {};
  theadElements?: (string | undefined)[];
}) => {
  return renderIndusPopupContainer(title, titleValue, tableElements, theadElements);
};

// Composant HYDRO Popup Container utilisant les fonctions de rendu existantes
export const HydroPopupContainer = ({
  title,
  titleValue,
  tableElements,
}: {
  title: string | (string | undefined)[];
  titleValue?: string;
  tableElements?: {};
}) => {
  return renderHydroPopupContainer(title, titleValue, tableElements);
};

// Composant H2S Popup Container utilisant les fonctions de rendu existantes
export const H2sPopupContainer = ({
  title,
  titleValue,
  tableElements,
  indicator,
  dateRemontee,
}: {
  title: string | (string | undefined)[];
  titleValue?: string;
  tableElements?: {};
  indicator?: string | null;
  dateRemontee?: string;
}) => {
  return renderH2sPopupContainer(title, titleValue, tableElements, indicator, dateRemontee);
};

// Composant ECP Popup Container utilisant les fonctions de rendu existantes
export const EcpPopupContainer = ({
  title,
  titleValue,
  tableElements,
  theadElements,
  iliElements,
  className,
  module,
  indicator,
  dateRemontee,
}: {
  title: string | (string | undefined)[];
  titleValue?: string;
  tableElements?: {};
  theadElements?: (string | undefined)[];
  iliElements?: any[];
  className?: string;
  module?: string;
  indicator?: string | null;
  dateRemontee?: string;
}) => {
  return renderEcpPopupContainer(title, titleValue, tableElements, theadElements, iliElements, className, module, indicator, dateRemontee);
};
